import { useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { openCallReason, openPrivacy, scrollToId } from "@/lib/site";
import { UNITA_TOTALI } from "@/components/site/Strutture";

const TITLE_LINES = ["La tua struttura", "è online.", "Ma chi la sta", "davvero seguendo?"];

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { scrollY } = useScroll();
  const watermarkY = useTransform(scrollY, [0, 700], [0, 180]);
  const reduceMotion = useReducedMotion();
  const [link, setLink] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [consent, setConsent] = useState(false);
  const [stage, setStage] = useState("link");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const enter = (delay, extra = {}) =>
    reduceMotion
      ? { initial: false, animate: { opacity: 1, y: 0 }, transition: { duration: 0 } }
      : { initial: { opacity: 0, y: 20, ...extra }, animate: { opacity: 1, y: 0 }, transition: { delay, duration: 0.8, ease: EASE } };

  const handleEvaluate = () => {
    if (!link.trim() || !link.includes(".") || link.includes(" ")) {
      setError("Incolla prima il link del tuo annuncio: lo trovi nella barra in alto quando apri la tua pagina su Booking o Airbnb.");
      toast.error("Incolla un link valido al tuo annuncio.");
      return;
    }
    setError("");
    setStage("contact");
  };

  const handleSubmit = async () => {
    if (!/^\+?[0-9\s.-]{8,18}$/.test(whatsapp.trim())) {
      setError("Inserisci il tuo numero WhatsApp, con prefisso: per esempio +39 347 1234567.");
      toast.error("Inserisci un numero WhatsApp valido.");
      return;
    }
    if (!consent) {
      setError("Per inviare serve spuntare la casella del consenso privacy qui sopra.");
      toast.error("Serve il consenso privacy per inviare la richiesta.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/valuation-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listing_url: link.trim(), whatsapp: whatsapp.trim(), consent }),
      });
      if (!res.ok) throw new Error();
      setStage("done");
    } catch {
      setError("Qualcosa non ha funzionato. Riprova tra poco, o scrivimi direttamente su WhatsApp col pulsante verde.");
      toast.error("Qualcosa non ha funzionato. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="hero-section" data-testid="hero-section" className="relative overflow-hidden border-b border-[#2A241F] pt-20 pb-14 sm:pt-28">
      <motion.div
        aria-hidden
        style={{ y: watermarkY }}
        className="text-outline pointer-events-none absolute -right-8 top-10 select-none font-heading text-[36vw] font-black leading-none tracking-tighter lg:text-[24vw]"
      >
        YU
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.p {...enter(0.9)} className="mb-4 section-kicker hero-kicker sm:mb-6">
          Per affitti brevi e strutture ricettive
        </motion.p>

        <h1 data-testid="hero-title" className="font-heading text-[1.7rem] font-black uppercase leading-[1.02] tracking-tighter text-[#FAF7F2] sm:text-5xl sm:leading-[0.98] lg:text-6xl">
          {TITLE_LINES.map((line, i) => (
            <span key={line} className="block pb-1">
              <motion.span
                className="block"
                initial={reduceMotion ? false : { opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: reduceMotion ? 0 : 0.15 + i * 0.12, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p {...enter(0.85)} data-testid="hero-intro-mobile" className="hero-intro mt-4 max-w-xl font-semibold text-[#FAF7F2] sm:hidden">
          Ogni giorno mi occupo io di prezzi e calendario di case vacanza e B&B in Sicilia. Costo fisso, zero percentuali sugli incassi.
        </motion.p>
        <motion.p {...enter(0.9)} data-testid="hero-intro-mobile-2" className="mt-2 max-w-xl text-base font-normal leading-relaxed text-[#C4BBB0] sm:hidden">
          E ti segue una persona vera, non un'agenzia anonima.
        </motion.p>
        <motion.p {...enter(0.85)} className="mt-6 hidden max-w-xl text-base font-light leading-relaxed text-[#C4BBB0] sm:block sm:text-lg">
          Sistemo ogni giorno prezzi e calendario della tua struttura per farti guadagnare di più. Costo fisso, niente percentuali sui tuoi incassi. E ti segue una persona vera, non un'agenzia anonima.
        </motion.p>

        <motion.div {...enter(0.95)} className="mt-4 flex items-center gap-4 sm:mt-8 sm:gap-5">
          <div data-testid="hero-luciano-photo" className="shrink-0 -rotate-3 border-4 border-[#F7F4EC] bg-[#F7F4EC] shadow-[0_12px_30px_rgba(0,0,0,0.45)]">
            <img
              src="/images/luciano.jpg"
              alt="Luciano, fondatore di YU YieldUp"
              className="h-16 w-14 object-cover grayscale contrast-110 sm:h-28 sm:w-24"
            />
          </div>
          <p className="max-w-[240px] text-sm leading-snug text-[#C4BBB0]">
            <span className="font-medium text-[#FAF7F2]">Luciano, fondatore di YU YieldUp.</span> <span className="hidden sm:inline">Quello che risponde quando scrivi.</span><span className="sm:hidden">Revenue manager.</span>
          </p>
        </motion.div>

        <motion.div {...enter(1)} className="mt-5 max-w-2xl sm:mt-8">
          {stage === "done" ? (
            <div data-testid="listing-success-message" className="border border-[#383029] p-6">
              <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Ricevuto.</p>
              <p className="mt-2 text-sm text-[#C4BBB0]">Ti scrivo su WhatsApp entro 48 ore lavorative: un video di 5 minuti con quello che migliorerei nel tuo annuncio, spiegato da me.</p>
            </div>
          ) : (
            <div data-testid="listing-evaluator-form" className="flex flex-col gap-3">
              <p className="section-kicker">Valutazione gratuita del tuo annuncio</p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="listing-url-input"
                  type="text"
                  value={link}
                  onChange={(e) => { setLink(e.target.value); setError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleEvaluate()}
                  placeholder="Link annuncio Booking o Airbnb"
                  className="w-full flex-1 rounded-full border border-[#4D2C20] bg-[#16110E] px-6 py-4 text-sm text-[#FAF7F2] placeholder-[#A89F91] outline-none transition-colors duration-300 focus:border-[#E05A2C]"
                />
                <button
                  data-testid="listing-evaluate-button"
                  onClick={handleEvaluate}
                  className="btn-glow shrink-0 rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98]"
                >
                  Fai valutare
                </button>
              </div>
              {error && (
                <p data-testid="listing-error" className="border-l-2 border-[#E05A2C] pl-4 text-sm leading-relaxed text-[#E05A2C]">
                  {error}
                </p>
              )}
              <p data-testid="listing-promise" className="text-sm leading-relaxed text-[#C4BBB0]">
                Entro 48 ore ti arriva su <span className="font-medium text-[#FAF7F2]">WhatsApp</span> un video di 5 minuti: quello che migliorerei nel tuo annuncio, spiegato da me.
              </p>
              <AnimatePresence>
                {stage === "contact" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <p className="mb-3 mt-2 section-kicker">Il tuo numero WhatsApp</p>
                    <div className="flex flex-col gap-3 sm:flex-row">
                      <input
                        data-testid="listing-whatsapp-input"
                        type="tel"
                        value={whatsapp}
                        onChange={(e) => { setWhatsapp(e.target.value); setError(""); }}
                        onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                        placeholder="Es. +39 347 1234567"
                        className="w-full flex-1 rounded-full border border-[#383029] bg-transparent px-6 py-4 text-sm text-[#FAF7F2] placeholder-[#A89F91] outline-none transition-colors duration-300 focus:border-[#E05A2C]"
                      />
                      <button
                        data-testid="listing-submit-button"
                        onClick={handleSubmit}
                        disabled={sending}
                        className="btn-glow shrink-0 rounded-full border border-[#383029] px-8 py-4 text-sm font-medium text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] active:scale-[0.98] disabled:opacity-50"
                      >
                        {sending ? "Invio…" : "Invia richiesta"}
                      </button>
                    </div>
                    <label className="mt-4 flex max-w-lg cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#A89F91]">
                      <input
                        data-testid="listing-consent-checkbox"
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#E05A2C]"
                      />
                      <span>
                        Ho letto l'{" "}
                        <button
                          type="button"
                          data-testid="listing-privacy-link"
                          onClick={(e) => { e.preventDefault(); openPrivacy(); }}
                          className="underline decoration-[#383029] underline-offset-2 transition-colors duration-300 hover:text-[#E05A2C]"
                        >
                          informativa privacy
                        </button>{" "}
                        e acconsento a essere ricontattato su WhatsApp per questa valutazione.
                      </span>
                    </label>
                  </motion.div>
                )}
              </AnimatePresence>
              <p data-testid="hero-proof-line" className="mt-1 font-mono text-xs uppercase tracking-widest text-[#A89F91] sm:text-sm">
                8 strutture in vetrina · {UNITA_TOTALI}+ unità seguite · media 9,1 su Booking · oltre 2.500 recensioni vere
              </p>
              <p data-testid="hero-reassurance" className="hidden max-w-lg text-sm leading-relaxed text-[#A89F91] sm:block">
                Uno specialista guarda davvero il tuo annuncio: se trova criticità te le segnala, se è tutto in ordine te lo dico senza inventare problemi. Sincero, sempre.
              </p>
            </div>
          )}
        </motion.div>

        <motion.div {...enter(1.1)} className="mt-6">
          <button
            data-testid="hero-open-soon-link"
            onClick={() => scrollToId("percorso-su-misura")}
            className="rounded-full border border-[#E05A2C] px-7 py-3.5 text-sm font-medium text-[#E05A2C] transition-colors duration-300 hover:bg-[#E05A2C] hover:text-white active:scale-[0.98]"
          >
            Stai per aprire la tua struttura? Parti da qui
          </button>
        </motion.div>

        <motion.div {...enter(1.15)} className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { n: "01", label: "I nostri servizi", testid: "hero-tile-services", onClick: () => scrollToId("servizi") },
            { n: "02", label: "Le nostre consulenze", testid: "hero-tile-consulenze", onClick: () => openCallReason("hero-consulenze") },
            { n: "03", label: "30 minuti con Luciano", testid: "hero-tile-call", primary: true, onClick: () => openCallReason("hero") },
          ].map((tile) => {
            const primary = Boolean(tile.href || tile.primary);
            const cls = primary
              ? "btn-glow group flex items-center justify-between gap-4 bg-[#E05A2C] p-7 text-left transition-colors duration-300 hover:bg-[#F26B3A] sm:p-8"
              : "group flex items-center justify-between gap-4 border border-[#383029] p-7 text-left transition-colors duration-300 hover:border-[#E05A2C] hover:bg-[#1C1815] sm:p-8";
            const inner = (
              <>
                <span>
                  <span className={`block font-heading text-3xl font-black tracking-tighter ${primary ? "text-white/70" : "text-[#E05A2C]"}`}>—</span>
                  <span className={`mt-2 block font-heading text-lg font-black uppercase leading-tight tracking-tight ${primary ? "text-white" : "text-[#FAF7F2]"}`}>{tile.label}</span>
                </span>
                <ArrowUpRight className={`h-6 w-6 shrink-0 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 ${primary ? "text-white" : "text-[#A89F91] group-hover:text-[#E05A2C]"}`} />
              </>
            );
            return tile.href ? (
              <a key={tile.n} data-testid={tile.testid} href={tile.href} target="_blank" rel="noopener noreferrer" className={cls}>
                {inner}
              </a>
            ) : (
              <button key={tile.n} data-testid={tile.testid} onClick={tile.onClick} className={cls}>
                {inner}
              </button>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
